// Be Bold - Session Builder
// Takes a week object, returns a session ready for the ExercisePlayer.
// Supports Year 1 (squeeze/relax/push) and Year 2 (sq/rx/px) field names.

const SCHEDULE_BY_FREQ = {
  3: { monday: 'ENDURANCE', tuesday: 'REST', wednesday: 'POWER', thursday: 'REST', friday: 'INTEGRATION', saturday: 'REST', sunday: 'REST' },
  4: { monday: 'ENDURANCE', tuesday: 'POWER', wednesday: 'REST', thursday: 'DIRECTION', friday: 'REST', saturday: 'INTEGRATION', sunday: 'REST' },
  5: { monday: 'ENDURANCE', tuesday: 'POWER', wednesday: 'RELEASE', thursday: 'REST', friday: 'DIRECTION', saturday: 'INTEGRATION', sunday: 'REST' },
  6: { monday: 'ENDURANCE', tuesday: 'POWER', wednesday: 'RELEASE', thursday: 'DIRECTION', friday: 'INTEGRATION', saturday: 'GENTLE', sunday: 'REST' },
}

// Normalize week fields to handle both Year 1 and Year 2 naming
// Week data can arrive with missing movement buckets (e.g. a REST
// week, a malformed legacy entry, or Path B overrides that only
// touch one movement). Return an empty object instead of undefined
// so callers can safely read .on / .reps / .hold / .cycles with
// `?.` + numeric defaults. Without this guard `sq.on !== undefined`
// and friends throw "Cannot read property of undefined."
function getSq(week) { return week?.squeeze || week?.sq || {} }
function getRx(week) { return week?.relax   || week?.rx || {} }
function getPx(week) { return week?.push    || week?.px || {} }

export function getSessionForDay(week, dayName, path = 'A') {
  const day = dayName.toLowerCase()
  const schedule = SCHEDULE_BY_FREQ[week.freq] || SCHEDULE_BY_FREQ[5]
  let sessionType = schedule[day] || 'REST'

  if (week.deload && sessionType !== 'REST') {
    sessionType = 'GENTLE'
  }

  let session = buildSession(week, sessionType)

  if (path === 'B' && (week.year || 1) === 1) {
    session = applyPathBOverrides(week, session)
  }

  return session
}

export function buildSession(week, sessionType) {
  const px = getPx(week)
  const pushHold = week.pushRule === 'direction_max_5s' ? Math.min(px.hold, 5)
    : week.pushRule === 'wave_4s_per_level' ? 4
    : Math.min(px.hold || 8, 8)

  return {
    type: sessionType,
    week: week.week,
    year: week.year || 1,
    deload: week.deload,
    color: week.color,
    position: week.position || null,
    plane: week.plane || null,
    catiaModule: week.catiaModule || false,
    exercises: buildExercises(week, sessionType, pushHold),
  }
}

function buildExercises(week, sessionType, pushHold) {
  const sq = getSq(week)
  const rx = getRx(week)
  const px = getPx(week)
  // Sane defaults keep a malformed or partial week from crashing
  // the builder mid-session. Falling back to conservative values
  // (hold 5s, 10 reps, 3 sets, 8 breath cycles) means a bad week
  // silently yields a short but valid session instead of a white
  // screen. Each field falls back only if undefined — a zero value
  // from the data is respected.
  const sqHold   = Number.isFinite(sq.hold)   ? sq.hold   : 5
  const sqReps   = Number.isFinite(sq.reps)   ? sq.reps   : 10
  const sqSets   = Number.isFinite(sq.sets)   ? sq.sets   : 3
  const rxCycles = Number.isFinite(rx.cycles) ? rx.cycles : 8
  const pxReps   = Number.isFinite(px.reps)   ? px.reps   : 8
  const isPulseWeek = sq.on !== undefined // squeeze defined as on/off pulse, not hold

  if (sessionType === 'REST') return []

  if (sessionType === 'ENDURANCE') return isPulseWeek
    ? [buildPulse(sqReps, sqSets), buildRelax(rxCycles), buildPush(pushHold, pxReps)]
    : [buildSqueeze(sqHold, sqReps, sqSets, 'endurance'), buildRelax(rxCycles), buildPush(pushHold, pxReps)]

  if (sessionType === 'POWER') return [
    buildPulse(sqReps || 20, sqSets || 3),
    buildRelax(rxCycles),
    buildPush(pushHold, 3),
  ]

  if (sessionType === 'RELEASE') return [
    buildRelax(Math.max(rxCycles, 12)),
    buildPush(pushHold, pxReps),
    buildSqueeze(3, 5, 1, 'light'),
  ]

  if (sessionType === 'DIRECTION') return isPulseWeek
    ? [buildPulse(sqReps, sqSets), buildRelax(rxCycles), buildPush(Math.min(pushHold, 5), pxReps)]
    : [buildDirection(sqHold, sqReps, sqSets, Math.min(pushHold, 5), rxCycles)]

  if (sessionType === 'INTEGRATION') return isPulseWeek
    ? [buildPulse(sqReps, sqSets), buildRelax(rxCycles), buildEightCountPush()]
    : [buildSqueeze(sqHold, sqReps, sqSets, 'named'), buildPulse(20, 2), buildRelax(rxCycles), buildEightCountPush()]

  if (sessionType === 'GENTLE') {
    const gentleHold = isPulseWeek ? 0 : Math.floor(sqHold * 0.5)
    return isPulseWeek
      ? [buildPulse(Math.floor(sqReps * 0.5), sqSets), buildRelax(rxCycles), buildPush(Math.floor(pushHold * 0.5), pxReps)]
      : [buildSqueeze(gentleHold, sqReps, sqSets, 'gentle'), buildRelax(rxCycles), buildPush(Math.floor(pushHold * 0.5), pxReps)]
  }

  return []
}

function buildSqueeze(hold, reps, sets, variant = 'standard') {
  return {
    movement: 'SQUEEZE',
    movementMode: 'squeeze',
    type: 'hold',
    hold,
    relax: hold,
    reps,
    sets,
    rest: 10,
    variant,
    timerCue: 'Close.\nSqueeze.\nHold.',
    timerCue_pt: 'Fechar.\nApertar.\nSegurar.',
    breathe: 'Exhale to start. Inhale to release.',
    breathe_pt: 'Expire para iniciar. Inspire para soltar.',
  }
}

function buildRelax(cycles) {
  return {
    movement: 'RELAX',
    movementMode: 'relax',
    type: 'hold',
    hold: 4,
    relax: 6,
    reps: cycles,
    sets: 1,
    rest: 0,
    timerCue: 'Inhale.\nDrop.\nExhale.\nStay soft.',
    timerCue_pt: 'Inspire.\nDescer.\nExpire.\nFique mole.',
    breathe: 'Inhale 4 counts, floor drops. Exhale 6 counts, stay completely soft.',
    breathe_pt: 'Inspire 4 tempos, assoalho desce. Expire 6 tempos, fique completamente mole.',
  }
}

function buildPush(hold, reps) {
  return {
    movement: 'PUSH',
    movementMode: 'push',
    type: 'hold',
    hold,
    relax: hold,
    reps,
    sets: 1,
    rest: 0,
    timerCue: 'Push gently.\nReturn.',
    timerCue_pt: 'Empurrar suave.\nRetornar.',
    breathe: 'Exhale to push. Inhale to return.',
    breathe_pt: 'Expire para empurrar. Inspire para retornar.',
  }
}

function buildPulse(reps, sets) {
  return {
    movement: 'SQUEEZE',
    movementMode: 'squeeze',
    type: 'pulse',
    reps,
    sets,
    on: 1,
    off: 1,
    rest: 15,
    timerCue: 'Close.\nSqueeze.\nRelease.',
    timerCue_pt: 'Fechar.\nApertar.\nSoltar.',
    breathe: 'Exhale to close and squeeze. Inhale to release.',
    breathe_pt: 'Expire para fechar e apertar. Inspire para soltar.',
  }
}

function buildDirection(hold, reps, sets, pushHold, relaxCycles) {
  return {
    movement: 'DIRECTION',
    movementMode: 'squeeze',
    type: 'alt',
    altA: 'Front wall',
    altB: 'Back wall',
    altA_pt: 'Parede frontal',
    altB_pt: 'Parede posterior',
    hold,
    relax: 4,
    reps,
    sets,
    rest: 0,
    squeeze: { hold, reps, sets },
    relax_cycles: relaxCycles,
    push: { hold: pushHold, reps },
    timerCue: 'Front.\nBack.\nRelax.',
    timerCue_pt: 'Frente.\nFundo.\nSoltar.',
  }
}

function buildEightCountPush() {
  return {
    movement: 'PUSH',
    movementMode: 'push',
    type: 'timed',
    hold: 120,
    sets: 1,
    rest: 0,
    timerCue: 'Contract.\n8 counts down.\nFull release.',
    timerCue_pt: 'Contrair.\n8 tempos descendo.\nSoltar completamente.',
    breathe: 'Exhale on the descent. Inhale to release.',
    breathe_pt: 'Expire na descida. Inspire para soltar.',
  }
}

// -- PATH B OVERRIDES (Year 1 only) -----------------------------------
export function applyPathBOverrides(weekObj, session) {
  const wk = weekObj.week

  if (wk <= 4) {
    if (session.type === 'REST') return session
    return {
      ...session,
      type: 'RELEASE',
      pathBStage: 'release_only',
      exercises: [
        buildRelax(15),
      ],
    }
  }

  if (wk >= 5 && wk <= 8) {
    return {
      ...session,
      pathBStage: 'gentle_intro',
      exercises: session.exercises.map(ex => {
        // Guards: fall back to sensible defaults if the upstream
        // exercise is missing a numeric field. NaN propagation
        // here used to surface as stuck timers mid-session.
        if (ex.movement === 'SQUEEZE') return { ...ex, hold: Math.max(2, Math.floor((ex.hold ?? 5) / 3)), reps: Math.max(5, Math.floor((ex.reps ?? 10) / 2)), effort: 20 }
        if (ex.movement === 'PUSH')    return { ...ex, hold: Math.min(4, ex.hold ?? 4), effort: 20 }
        if (ex.movement === 'RELAX')   return { ...ex, reps: (ex.reps ?? 8) + 2 }
        return ex
      }),
    }
  }

  if (wk >= 9 && wk <= 12) {
    return {
      ...session,
      pathBStage: 'building',
      exercises: session.exercises.map(ex => {
        if (ex.movement === 'SQUEEZE') return { ...ex, hold: Math.max(3, Math.floor((ex.hold ?? 5) * 0.6)), sets: Math.max(2, (ex.sets ?? 3) - 1), effort: 30 }
        if (ex.movement === 'RELAX')   return { ...ex, reps: (ex.reps ?? 8) + 1 }
        return ex
      }),
    }
  }

  // Week 13 onward: Path B joins Path A fully
  return session
}

// -- YEAR 2 SESSION BUILDER --------------------------------------------
export function getYear2Session(week, dayName) {
  const day = dayName.toLowerCase()
  const sched = SCHEDULE_BY_FREQ[week.freq] || SCHEDULE_BY_FREQ[5]
  let type = sched[day] || 'REST'
  if (week.deload && type !== 'REST') type = 'GENTLE'

  const px = getPx(week)
  const pushHold = week.pushRule === 'direction_max_5s' ? Math.min(px.hold, 5)
    : week.pushRule === 'wave_4s_per_level' ? 4
    : Math.min(px.hold || 8, 8)

  const exercises = buildYear2Exercises(week, type, pushHold)

  return { type, week: week.week, year: 2, deload: week.deload, color: week.color,
    position: week.position || null, plane: week.plane || null, catiaModule: week.catiaModule || false, exercises }
}

function buildYear2Exercises(week, type, pushHold) {
  if (type === 'REST') return []
  if (type === 'POWER') {
    const sq = getSq(week)
    const rx = getRx(week)
    return [
      buildPulse(sq.reps || 20, sq.sets || 3),
      buildRelax(rx.cycles),
      buildPush(pushHold, 3),
    ]
  }
  return buildExercises(week, type, pushHold)
}

// -- POSITION AND PLANE LABELS -----------------------------------------
export const POSITION_LABELS = {
  sitting:             { en: 'Sitting',            pt: 'Sentada' },
  supine:              { en: 'Lying Down',          pt: 'Deitada' },
  standing:            { en: 'Standing',            pt: 'Em Pé' },
  kneeling:            { en: 'Kneeling',            pt: 'Ajoelhada' },
  kneeling_straddling: { en: 'Functional Position', pt: 'Posição Funcional' },
}

export const PLANE_LABELS = {
  vertical:           { en: 'Vertical Plane',     pt: 'Plano Vertical' },
  lateral:            { en: 'Lateral Plane',      pt: 'Plano Lateral' },
  sagittal:           { en: 'Sagittal Plane',     pt: 'Plano Sagital' },
  rotational:         { en: 'Rotational Plane',   pt: 'Plano Rotacional' },
  diagonal:           { en: 'Diagonal Plane',     pt: 'Plano Diagonal' },
  vertical_lateral:   { en: 'Vertical + Lateral', pt: 'Vertical + Lateral' },
  vertical_sagittal:  { en: 'Vertical + Sagittal',pt: 'Vertical + Sagital' },
  lateral_sagittal:   { en: 'Lateral + Sagittal', pt: 'Lateral + Sagital' },
  diagonal_vertical:  { en: 'Diagonal + Vertical',pt: 'Diagonal + Vertical' },
  multiplanar:        { en: 'Multiplanar',         pt: 'Multiplanar' },
  timing:             { en: 'Timing Coordination', pt: 'Coordenacao de Timing' },
  all:                { en: '3D Coordination',     pt: 'Coordenacao 3D' },
}
